import {
  AUTH_CHALLENGE_LIMITS,
  DEFAULT_AUTH_CHALLENGE_SETTINGS,
  type AuthChallengeSettings,
} from '@/types/organization';

/**
 * Per-org auth challenge (expiry) settings helper.
 *
 * Mirrors `lib/sms/twilioConfig.ts`: a normalise-with-defaults reader over the
 * JSONB `organizations.settings` blob. Every value is CLAMPED to a hard-coded
 * min/max so a malformed or hostile stored value (e.g. 0 or 999999) silently
 * resolves to a safe bound rather than being honoured. Only expiry is exposed
 * per org — security-critical controls stay central.
 */

/** Coerce an arbitrary value to an integer minute count clamped to [min, max]. */
export function clampMinutes(
  value: unknown,
  min: number,
  max: number,
  fallback: number,
): number {
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return fallback;
  const rounded = Math.round(num);
  if (rounded < min) return min;
  if (rounded > max) return max;
  return rounded;
}

/** Normalise a raw `auth_challenge_settings` value into a clamped, complete object. */
export function normaliseAuthChallengeSettings(raw: unknown): AuthChallengeSettings {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...DEFAULT_AUTH_CHALLENGE_SETTINGS };
  }

  const obj = raw as Record<string, unknown>;
  return {
    email_magic_link_expiry_minutes: clampMinutes(
      obj.email_magic_link_expiry_minutes,
      AUTH_CHALLENGE_LIMITS.email_magic_link_expiry_minutes.min,
      AUTH_CHALLENGE_LIMITS.email_magic_link_expiry_minutes.max,
      DEFAULT_AUTH_CHALLENGE_SETTINGS.email_magic_link_expiry_minutes,
    ),
    sms_otp_expiry_minutes: clampMinutes(
      obj.sms_otp_expiry_minutes,
      AUTH_CHALLENGE_LIMITS.sms_otp_expiry_minutes.min,
      AUTH_CHALLENGE_LIMITS.sms_otp_expiry_minutes.max,
      DEFAULT_AUTH_CHALLENGE_SETTINGS.sms_otp_expiry_minutes,
    ),
    password_reset_expiry_minutes: clampMinutes(
      obj.password_reset_expiry_minutes,
      AUTH_CHALLENGE_LIMITS.password_reset_expiry_minutes.min,
      AUTH_CHALLENGE_LIMITS.password_reset_expiry_minutes.max,
      DEFAULT_AUTH_CHALLENGE_SETTINGS.password_reset_expiry_minutes,
    ),
    sms_otp_resend_cooldown_seconds: clampMinutes(
      obj.sms_otp_resend_cooldown_seconds,
      AUTH_CHALLENGE_LIMITS.sms_otp_resend_cooldown_seconds.min,
      AUTH_CHALLENGE_LIMITS.sms_otp_resend_cooldown_seconds.max,
      DEFAULT_AUTH_CHALLENGE_SETTINGS.sms_otp_resend_cooldown_seconds,
    ),
    sms_otp_send_window_minutes: clampMinutes(
      obj.sms_otp_send_window_minutes,
      AUTH_CHALLENGE_LIMITS.sms_otp_send_window_minutes.min,
      AUTH_CHALLENGE_LIMITS.sms_otp_send_window_minutes.max,
      DEFAULT_AUTH_CHALLENGE_SETTINGS.sms_otp_send_window_minutes,
    ),
    sms_otp_max_sends_per_window: clampMinutes(
      obj.sms_otp_max_sends_per_window,
      AUTH_CHALLENGE_LIMITS.sms_otp_max_sends_per_window.min,
      AUTH_CHALLENGE_LIMITS.sms_otp_max_sends_per_window.max,
      DEFAULT_AUTH_CHALLENGE_SETTINGS.sms_otp_max_sends_per_window,
    ),
    email_magic_link_resend_cooldown_seconds: clampMinutes(
      obj.email_magic_link_resend_cooldown_seconds,
      AUTH_CHALLENGE_LIMITS.email_magic_link_resend_cooldown_seconds.min,
      AUTH_CHALLENGE_LIMITS.email_magic_link_resend_cooldown_seconds.max,
      DEFAULT_AUTH_CHALLENGE_SETTINGS.email_magic_link_resend_cooldown_seconds,
    ),
    email_magic_link_send_window_minutes: clampMinutes(
      obj.email_magic_link_send_window_minutes,
      AUTH_CHALLENGE_LIMITS.email_magic_link_send_window_minutes.min,
      AUTH_CHALLENGE_LIMITS.email_magic_link_send_window_minutes.max,
      DEFAULT_AUTH_CHALLENGE_SETTINGS.email_magic_link_send_window_minutes,
    ),
    email_magic_link_max_sends_per_window: clampMinutes(
      obj.email_magic_link_max_sends_per_window,
      AUTH_CHALLENGE_LIMITS.email_magic_link_max_sends_per_window.min,
      AUTH_CHALLENGE_LIMITS.email_magic_link_max_sends_per_window.max,
      DEFAULT_AUTH_CHALLENGE_SETTINGS.email_magic_link_max_sends_per_window,
    ),
  };
}

/** Read clamped auth challenge settings from an `organizations.settings` blob. */
export function getAuthChallengeSettings(settings: unknown): AuthChallengeSettings {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return { ...DEFAULT_AUTH_CHALLENGE_SETTINGS };
  }

  return normaliseAuthChallengeSettings(
    (settings as Record<string, unknown>).auth_challenge_settings,
  );
}

/**
 * Read the clamped platform-wide password-reset expiry from the platform org's
 * `organizations.settings` blob (stored under `settings.global.auth`). Governs
 * recovery links from the slug-less admin forgot-password flow (global admins).
 * Any missing/malformed value resolves to the safe default (60m).
 */
export function getGlobalPasswordResetExpiryMinutes(settings: unknown): number {
  const limits = AUTH_CHALLENGE_LIMITS.password_reset_expiry_minutes;
  const fallback = DEFAULT_AUTH_CHALLENGE_SETTINGS.password_reset_expiry_minutes;
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return fallback;
  }
  const global = (settings as Record<string, unknown>).global;
  if (!global || typeof global !== 'object' || Array.isArray(global)) {
    return fallback;
  }
  const auth = (global as Record<string, unknown>).auth;
  if (!auth || typeof auth !== 'object' || Array.isArray(auth)) {
    return fallback;
  }
  return clampMinutes(
    (auth as Record<string, unknown>).password_reset_expiry_minutes,
    limits.min,
    limits.max,
    fallback,
  );
}

/**
 * Validate a caller-supplied settings object (defence-in-depth on top of the
 * clamp). Returns an error message for out-of-range input, or null when valid.
 */
export function validateAuthChallengeSettings(
  settings: AuthChallengeSettings,
): string | null {
  const email = settings.email_magic_link_expiry_minutes;
  const sms = settings.sms_otp_expiry_minutes;
  const reset = settings.password_reset_expiry_minutes;
  const emailLimits = AUTH_CHALLENGE_LIMITS.email_magic_link_expiry_minutes;
  const smsLimits = AUTH_CHALLENGE_LIMITS.sms_otp_expiry_minutes;
  const resetLimits = AUTH_CHALLENGE_LIMITS.password_reset_expiry_minutes;

  if (
    !Number.isInteger(email)
    || email < emailLimits.min
    || email > emailLimits.max
  ) {
    return `Email magic-link expiry must be a whole number between ${emailLimits.min} and ${emailLimits.max} minutes.`;
  }

  if (!Number.isInteger(sms) || sms < smsLimits.min || sms > smsLimits.max) {
    return `SMS code expiry must be a whole number between ${smsLimits.min} and ${smsLimits.max} minutes.`;
  }

  if (
    !Number.isInteger(reset)
    || reset < resetLimits.min
    || reset > resetLimits.max
  ) {
    return `Password-reset expiry must be a whole number between ${resetLimits.min} and ${resetLimits.max} minutes.`;
  }

  // Resend-limit knobs (per channel): cooldown seconds, window minutes, and the
  // max sends per window. Each is validated against its own clamp bounds.
  const resendChecks: Array<{ value: number; min: number; max: number; label: string; unit: string }> = [
    {
      value: settings.sms_otp_resend_cooldown_seconds,
      ...AUTH_CHALLENGE_LIMITS.sms_otp_resend_cooldown_seconds,
      label: 'SMS resend cooldown',
      unit: 'seconds',
    },
    {
      value: settings.sms_otp_send_window_minutes,
      ...AUTH_CHALLENGE_LIMITS.sms_otp_send_window_minutes,
      label: 'SMS send window',
      unit: 'minutes',
    },
    {
      value: settings.sms_otp_max_sends_per_window,
      ...AUTH_CHALLENGE_LIMITS.sms_otp_max_sends_per_window,
      label: 'SMS max sends per window',
      unit: 'sends',
    },
    {
      value: settings.email_magic_link_resend_cooldown_seconds,
      ...AUTH_CHALLENGE_LIMITS.email_magic_link_resend_cooldown_seconds,
      label: 'Magic-link resend cooldown',
      unit: 'seconds',
    },
    {
      value: settings.email_magic_link_send_window_minutes,
      ...AUTH_CHALLENGE_LIMITS.email_magic_link_send_window_minutes,
      label: 'Magic-link send window',
      unit: 'minutes',
    },
    {
      value: settings.email_magic_link_max_sends_per_window,
      ...AUTH_CHALLENGE_LIMITS.email_magic_link_max_sends_per_window,
      label: 'Magic-link max sends per window',
      unit: 'sends',
    },
  ];

  for (const check of resendChecks) {
    if (
      !Number.isInteger(check.value)
      || check.value < check.min
      || check.value > check.max
    ) {
      return `${check.label} must be a whole number between ${check.min} and ${check.max} ${check.unit}.`;
    }
  }

  return null;
}

/**
 * Password-reset link validity (minutes) used for on-screen/template copy where
 * no per-org settings are loaded (e.g. the slug-less admin forgot-password
 * page). The real enforced TTL is the per-org / platform
 * `password_reset_expiry_minutes`; this constant is the single source for the
 * default copy so the wording can't drift from the default enforcement.
 */
export const PASSWORD_RESET_EXPIRY_MINUTES =
  DEFAULT_AUTH_CHALLENGE_SETTINGS.password_reset_expiry_minutes;

/**
 * Username-change verification link validity (minutes). Single source for both
 * the enforced `pending_username_expires_at` and the verification email copy.
 */
export const USERNAME_CHANGE_EXPIRY_MINUTES = 1440;

/**
 * Email-confirmation (signup) link validity (minutes). Mirrors the GoTrue
 * `mailer_otp_exp` default so the `{{expiry}}` token in the confirmation
 * templates can't drift from the enforced TTL.
 */
export const EMAIL_CONFIRMATION_EXPIRY_MINUTES = 1440;

/**
 * Render an expiry duration as human-readable copy for comms and on-screen
 * text (e.g. "10 minutes", "1 hour", "2 hours", "24 hours"). Single source of
 * truth so template/page copy can never drift from the real expiry.
 */
export function formatExpiryMinutes(minutes: number): string {
  const safe = Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : 0;
  if (safe < 60 || safe % 60 !== 0) {
    return `${safe} ${safe === 1 ? 'minute' : 'minutes'}`;
  }
  const hours = safe / 60;
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}
